import * as React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import HOST_URL from "../../../utils/Url";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
} from "@mui/x-data-grid";
import { useFormik } from "formik";
import * as Yup from "yup";

interface NewNode {
  id: number;
  nodeID: number;
  name: string;
  titleID: number;
  isActive?: boolean;
  serialNo?: number;
}

interface NewNodeFormData {
  id: number;
  nodeID: number;
  name: string;
  titleID: number;
}

export default function NewNodeMaster() {
  const { t } = useTranslation();
  const [newNodes, setNewNodes] = useState<NewNode[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedNode, setSelectedNode] = useState<NewNode | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Form validation schema
  const validationSchema = Yup.object({
    nodeID: Yup.number()
      .required(t("text.nodeIDRequired"))
      .min(0, t("text.nodeIDMinValue")),
    name: Yup.string()
      .required(t("text.nodeNameRequired"))
      .max(100, t("text.nodeNameMaxLength"))
      .matches(/^[a-zA-Z0-9\s\-&.,()]+$/, t("text.invalidNodeName"))
      .test(
        'no-special-chars-start-end',
        t("text.noSpecialCharsStartEnd"),
        function (value: string) {
          if (!value) return true;
          return !/^[^a-zA-Z0-9]|[^a-zA-Z0-9]$/.test(value);
        }
      ),
  });

  // Formik initialization
  const formik = useFormik<NewNodeFormData>({
    initialValues: {
      id: 0,
      nodeID: 0,
      name: "",
      titleID: 1, // Always 1 as per API requirement
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmitForm(values);
    },
  });

  useEffect(() => {
    fetchNewNodesData();
  }, []);

  const fetchNewNodesData = async () => {
    try {
      setIsLoading(true);
      const collectData = {
        id: -1,
        nodeID: -1,
        titleID: -1,
      };

      const response = await axios.post(
        HOST_URL.HOST_URL + `NewNodeMaster/GetNewNodeMaster`,
        collectData
      );

      if (response.data.isSuccess) {
        const data = response.data.data;
        const nodesWithIds = data.map((node: NewNode, index: number) => ({
          ...node,
          serialNo: index + 1,
          id: node.id,
        }));
        setNewNodes(nodesWithIds);
        setupColumns();
      } else {
        toast.error(response.data.mesg);
      }
    } catch (error) {
      console.error("Error fetching nodes:", error);
      toast.error(t("text.failedToLoadNodes"));
    } finally {
      setIsLoading(false);
    }
  };

  const setupColumns = () => {
    const columns: GridColDef[] = [
      {
        field: "actions",
        headerClassName: "MuiDataGrid-colCell",
        headerName: t("text.Action"),
        width: 180,
        renderCell: (params) => {
          return (
            <Stack
              spacing={1}
              direction="row"
              sx={{ alignItems: "center" }}
            >
              <EditIcon
                style={{
                  fontSize: "20px",
                  color: "blue",
                  cursor: "pointer",
                }}
                className="cursor-pointer"
                onClick={() => handleEditClick(params.row)}
                titleAccess={t("text.edit")}
              />
              <DeleteIcon
                style={{
                  fontSize: "20px",
                  color: "red",
                  cursor: "pointer",
                }}
                onClick={() => {
                  handleDeleteClick(params.row.id);
                }}
                titleAccess={t("text.delete")}
              />
            </Stack>
          );
        },
      },
      {
        field: "serialNo",
        headerName: t("text.SrNo"),
        flex: 0.5,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 80,
      },
      {
        field: "nodeID",
        headerName: t("text.NodeID"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 120,
      },
      {
        field: "name",
        headerName: t("text.NodeName"),
        flex: 2,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 250,
      },
    ];
    setColumns(columns);
  };

  const handleAddClick = () => {
    setIsFormVisible(true);
    setIsEditMode(false);
    setSelectedNode(null);
    formik.resetForm({
      values: {
        id: 0,
        nodeID: 0,
        name: "",
        titleID: 1,
      },
    });
  };

  const handleEditClick = (node: NewNode) => {
    setIsFormVisible(true);
    setIsEditMode(true);
    setSelectedNode(node);
    formik.setValues({
      id: node.id,
      nodeID: node.nodeID,
      name: node.name,
      titleID: node.titleID,
    });
  };

  const handleCancelForm = () => {
    setIsFormVisible(false);
    setIsEditMode(false);
    setSelectedNode(null);
    formik.resetForm();
  };

  const handleSubmitForm = async (values: NewNodeFormData) => {
    setFormSubmitting(true);

    try {
      // Prepare data for API - titleID is always 1
      const submitData = {
        id: isEditMode ? values.id : 0,
        nodeID: values.nodeID,
        name: values.name.trim(),
        titleID: 1, // Always set to 1 as per API requirement
      };

      const response = await axios.post(
        HOST_URL.HOST_URL + `NewNodeMaster/AddUpdateNewNodeMaster`,
        submitData
      );

      if (response.data.isSuccess) {
        toast.success(
          isEditMode
            ? t("text.nodeUpdatedSuccessfully")
            : t("text.nodeAddedSuccessfully")
        );
        setIsFormVisible(false);
        fetchNewNodesData();
      } else {
        toast.error(response.data.mesg || t("text.operationFailed"));
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data?.mesg || t("text.badRequest"));
      } else {
        toast.error(t("text.somethingWentWrong"));
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  const acceptDelete = async (id: number) => {
    try {
      const collectData = {
        id: id,
      };

      const response = await axios.post(
        HOST_URL.HOST_URL + `NewNodeMaster/DeleteNewNodeMaster`,
        collectData
      );

      if (response.data.isSuccess) {
        toast.success(t("text.nodeDeletedSuccessfully"));
        fetchNewNodesData();
      } else {
        toast.error(response.data.mesg || t("text.deleteFailed"));
      }
    } catch (error: any) {
      console.error("Error deleting node:", error);
      if (error.response?.status === 404) {
        toast.error(t("text.nodeNotFound"));
      } else {
        toast.error(t("text.deleteFailed"));
      }
    }
  };

  const rejectDelete = () => {
    toast.warn(t("text.deleteCancelled"), { autoClose: 3000 });
  };

  const handleDeleteClick = (id: number) => {
    confirmDialog({
      message: t("text.deleteConfirmationMessage"),
      header: t("text.deleteConfirmation"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => acceptDelete(id),
      reject: rejectDelete,
    });
  };

  // Sanitize input function
  const sanitizeInput = (value: string) => {
    if (!value) return value;

    let sanitized = value.replace(/[^a-zA-Z0-9\s\-&.,()]/g, '');
    sanitized = sanitized.replace(/([\-\s&.,()]){2,}/g, '$1');
    sanitized = sanitized.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '');

    return sanitized;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply sanitization only for name field
    if (name === "name") {
      processedValue = sanitizeInput(value);
    }

    if (value !== processedValue) {
      formik.setFieldValue(name, processedValue);
    } else {
      formik.handleChange(e);
    }
  };

  return (
    <>
      <Card
        style={{
          width: "100%",
          backgroundColor: "#E9FDEE",
          border: ".5px solid #FF7722 ",
          marginTop: "3vh",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            "& .MuiDataGrid-colCell": {
              backgroundColor: `var(--grid-headerBackground)`,
              color: `var(--grid-headerColor)`,
              fontSize: 17,
              fontWeight: 900,
            },
          }}
          style={{ padding: "10px" }}
        >
          <ConfirmDialog />

          <Typography
            gutterBottom
            variant="h5"
            component="div"
            sx={{ padding: "20px" }}
            align="left"
          >
            {t("text.newNodeMaster")}
          </Typography>
          <Divider />

          <Box height={10} />

          {/* Add Node Button - Only show when form is not visible */}
          {!isFormVisible && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Button
                onClick={handleAddClick}
                variant="contained"
                endIcon={<AddCircleIcon />}
                size="large"
                style={{ backgroundColor: `var(--header-background)` }}
              >
                {t("text.addNewNode")}
              </Button>
            </Stack>
          )}

          {/* Form Section */}
          {isFormVisible && (
            <Card
              style={{
                marginBottom: "20px",
                padding: "20px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                style={{
                  color: `var(--header-background)`,
                  fontWeight: 600
                }}
              >
                {isEditMode ? t("text.editNode") : t("text.addNewNode")}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  {/* Node ID Field */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={
                        <span>
                          {t("text.nodeID")}
                          <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      name="nodeID"
                      type="number"
                      size="small"
                      fullWidth
                      value={formik.values.nodeID}
                      onChange={handleInputChange}
                      onBlur={formik.handleBlur}
                      disabled={formSubmitting}
                      error={formik.touched.nodeID && Boolean(formik.errors.nodeID)}
                      helperText={formik.touched.nodeID && formik.errors.nodeID}
                      inputProps={{
                        min: 0,
                        step: 1
                      }}
                    />
                  </Grid>

                  {/* Node Name Field */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={
                        <span>
                          {t("text.nodeName")}
                          <span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      name="name"
                      size="small"
                      fullWidth
                      value={formik.values.name}
                      onChange={handleInputChange}
                      onBlur={formik.handleBlur}
                      disabled={formSubmitting}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      inputProps={{
                        maxLength: 100,
                        placeholder: t("text.enterNodeName")
                      }}
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={formSubmitting || !formik.isValid}
                        style={{
                          backgroundColor: `var(--header-background)`,
                          minWidth: "120px"
                        }}
                      >
                        {formSubmitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : isEditMode ? (
                          t("text.update")
                        ) : (
                          t("text.save")
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelForm}
                        disabled={formSubmitting}
                        style={{
                          color: `var(--header-background)`,
                          borderColor: `var(--header-background)`,
                          minWidth: "120px"
                        }}
                      >
                        {t("text.cancel")}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            </Card>
          )}

          {/* Data Grid Section */}
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "300px",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <Box>
              <br />
              <div style={{ width: "100%", backgroundColor: `var(--grid-background)` }}>
                <DataGrid
                  rows={newNodes}
                  columns={columns}
                  autoHeight
                  slots={{
                    toolbar: GridToolbar,
                  }}
                  rowSpacingType="border"
                  pagination={true}
                  pageSizeOptions={[5, 10, 25, 50, 100].map((size) => ({
                    value: size,
                    label: `${size}`,
                  }))}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                    },
                  }}
                  getRowId={(row) => row.id}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      padding: '8px',
                    },
                  }}
                />
              </div>
            </Box>
          )}
        </Paper>
      </Card>
      <ToastApp />
    </>
  );
}